// functions/Fx_ScheduleJobSeriesWrites.ts

import { collection, doc, writeBatch } from "firebase/firestore";
import { db } from "../firebase";
import { fx_DocStamp } from "./Fx_DocStamp";
import { fx_JobStamp } from "./Fx_JobStamp";
import { FB_COLLECTION_PATH } from "./postActionTypes";
import type {
  CreateSeriesJobDocsBatchArgs,
  OccurrenceRange,
} from "./Fx_ScheduleJobTypes";

/**
 * Parse "YYYY-MM-DDTHH:mm" or "YYYY-MM-DDTHH:mm:ss" as a local Date.
 * Note: new Date("YYYY-MM-DDTHH:mm") is treated as local time in modern browsers.
 */
const toLocalDate = (isoLocal: string): Date => {
  if (!isoLocal) return new Date(NaN);
  // Ensure seconds exist for consistent behavior
  const normalized =
    isoLocal.length === 16 ? `${isoLocal}:00` : isoLocal.substring(0, 19);
  return new Date(normalized);
};

/** Format a Date back to "YYYY-MM-DDTHH:mm:ss" local-ish string */
const toIsoLocalSeconds = (d: Date): string => {
  // We want a stable local "datetime-local-ish" string
  // Using toISOString() would convert to UTC, so we manually build local components.
  const pad = (n: number) => String(n).padStart(2, "0");
  const yyyy = d.getFullYear();
  const mm = pad(d.getMonth() + 1);
  const dd = pad(d.getDate());
  const hh = pad(d.getHours());
  const mi = pad(d.getMinutes());
  const ss = pad(d.getSeconds());
  return `${yyyy}-${mm}-${dd}T${hh}:${mi}:${ss}`;
};

const addDays = (isoLocal: string, days: number): string => {
  const d = toLocalDate(isoLocal);
  if (!Number.isFinite(d.getTime())) return isoLocal;
  d.setDate(d.getDate() + days);
  return toIsoLocalSeconds(d);
};

const minutesBetween = (startIsoLocal: string, endIsoLocal: string): number => {
  const a = toLocalDate(startIsoLocal).getTime();
  const b = toLocalDate(endIsoLocal).getTime();
  if (!Number.isFinite(a) || !Number.isFinite(b)) return 0;
  return Math.max(0, Math.round((b - a) / 60000));
};

const addMinutes = (isoLocal: string, minutes: number): string => {
  const d = toLocalDate(isoLocal);
  if (!Number.isFinite(d.getTime())) return isoLocal;
  d.setMinutes(d.getMinutes() + minutes);
  return toIsoLocalSeconds(d);
};

const buildSeriesOccurrences = (params: {
  repeatPattern: "daily" | "weekly";
  seriesStartIsoLocal: string;
  endSelectedDate: string;
  occurrences: number;
  seriesIndexStart: number;
}): OccurrenceRange[] => {
  const {
    repeatPattern,
    seriesStartIsoLocal,
    endSelectedDate,
    occurrences,
    seriesIndexStart,
  } = params;

  const stepDays = repeatPattern === "weekly" ? 7 : 1;

  // Preserve duration from the current form values
  const durationMins = minutesBetween(seriesStartIsoLocal, endSelectedDate);

  const out: OccurrenceRange[] = [];
  const safeOccurrences = Math.max(0, Math.floor(occurrences || 0));

  for (let i = 0; i < safeOccurrences; i++) {
    const start = addDays(seriesStartIsoLocal, stepDays * i);
    const end = addMinutes(start, durationMins);

    out.push({
      startIsoLocal: start,
      endIsoLocal: end,
      seriesIndex: seriesIndexStart + i,
    });
  }

  return out;
};

// ----------------------------
// Overloads (so you can call old or new style)
// ----------------------------
export async function createSeriesJobDocsBatch(
  args: CreateSeriesJobDocsBatchArgs
): Promise<string[]>;

export async function createSeriesJobDocsBatch(
  profile: any,
  company: string,
  location: string,
  locationCoordinates: string,
  pay: string,
  jobId: string,
  notes: string,
  numberOfWorkers: number,
  repeatPattern: "daily" | "weekly",
  seriesId: string,
  seriesStartIsoLocal: string,
  endSelectedDate: string,
  occurrences: number,
  seriesIndexStart: number,
  seriesTotal: number,
  jobStatusSched: string
): Promise<string[]>;

// Implementation (handles both call styles)
export async function createSeriesJobDocsBatch(
  a1: any,
  a2?: any,
  a3?: any,
  a4?: any,
  a5?: any,
  a6?: any,
  a7?: any,
  a8?: any,
  a9?: any,
  a10?: any,
  a11?: any,
  a12?: any,
  a13?: any,
  a14?: any,
  a15?: any,
  a16?: any
): Promise<string[]> {
  // Detect call style
  const args: CreateSeriesJobDocsBatchArgs =
    typeof a1 === "object" && a1 && "seriesId" in a1
      ? (a1 as CreateSeriesJobDocsBatchArgs)
      : {
          profile: a1,
          company: a2,
          location: a3,
          locationCoordinates: a4,
          pay: a5,
          jobId: a6,
          notes: a7,
          numberOfWorkers: a8,
          repeatPattern: a9,
          seriesId: a10,
          seriesStartIsoLocal: a11,
          endSelectedDate: a12,
          occurrences: a13,
          seriesIndexStart: a14,
          seriesTotal: a15,
          jobStatusSched: a16,
        };

  const {
    profile,
    company,
    location,
    locationCoordinates,
    pay,
    jobId,
    notes,
    numberOfWorkers,
    repeatPattern,
    seriesId,
    seriesStartIsoLocal,
    endSelectedDate,
    occurrences,
    seriesIndexStart,
    seriesTotal,
    jobStatusSched,
  } = args;

  if (!profile?.UserId) {
    throw new Error("createSeriesJobDocsBatch: missing profile.UserId");
  }
  if (!seriesId) {
    throw new Error("createSeriesJobDocsBatch: missing seriesId");
  }
  if (repeatPattern !== "daily" && repeatPattern !== "weekly") {
    throw new Error(
      `createSeriesJobDocsBatch: repeatPattern must be 'daily' or 'weekly' (got '${repeatPattern}')`
    );
  }

  const occ = buildSeriesOccurrences({
    repeatPattern,
    seriesStartIsoLocal,
    endSelectedDate,
    occurrences,
    seriesIndexStart,
  });

  const batch = writeBatch(db);
  const jobIds: string[] = [];

  for (const o of occ) {
    const newDocRef = doc(collection(db, FB_COLLECTION_PATH.JobsDocs));
    jobIds.push(newDocRef.id);

    const now = Date.now();

    const docStamp = fx_DocStamp(
      now,
      profile.UserId,
      profile.FirstName,
      profile.LastName,
      profile.ProfilePicURL,
      profile.UserId,
      profile.FirstName,
      profile.LastName,
      profile.ProfilePicURL,
      "NA",
      "NA",
      "NA",
      "NA",
      "NA",
      "NA"
    );

    const jobInfo = fx_JobStamp(
      newDocRef.id, // uniqueJobId
      company,
      o.startIsoLocal,
      o.endIsoLocal,
      location,
      locationCoordinates,
      pay,
      jobId,
      notes,
      numberOfWorkers,
      false,
      false,
      false,
      false,
      true,
      false,
      jobStatusSched,
      "",
      0
    );

    // Ensure series metadata is attached to core
    (jobInfo as any).core = (jobInfo as any).core || {};
    (jobInfo as any).core.seriesId = seriesId;
    (jobInfo as any).core.seriesIndex = o.seriesIndex;
    (jobInfo as any).core.seriesTotal = seriesTotal;

    batch.set(newDocRef, { docStamp, jobInfo });
  }

  await batch.commit();
  return jobIds;
}
